import * as Yup from "yup";
import Submit from "@/lib/form/formik/components/buttons/Submit";
import Cancel from "@/lib/form/formik/components/buttons/Cancel";
import { callApi } from "@/lib/apiClient/api";
import { showToast } from "@/lib/ui/actions/actions";
import {
  PLATFORM_TYPE_ANDROID,
  PLATFORM_TYPE_ANDROID_TV,
  PLATFORM_TYPE_GOOGLE_TV,
  PLATFORM_TYPE_TVOS,
  PLATFORM_TYPE_IOS,
  PLATFORM_TYPE_ROKU,
  PLATFORM_TYPE_COBRA,
  PLATFORM_TYPE_CUSTOM,
  PLATFORM_TYPE_EMBED_PLAYER,
  GATEWAY_TYPE_ITUNES,
  GATEWAY_TYPE_AMAZON,
  GATEWAY_TYPE_ROKU,
  GATEWAY_TYPE_STRIPE,
  GATEWAY_TYPE_TPAY,
  GATEWAY_TYPE_PAYPAL,
  GATEWAY_TYPE_AUTHORIZE
} from "@/config/constants";
import { validationMessages } from "@/lib/form/formik/validationSchema";

export const ownDomainValidation = Yup.string()
  .required(validationMessages.required)
  .max(65, "This value is too long. It should have 65 characters or less.")
  .matches(
    /^(([-a-z0-9])+\.)+([a-z]{2,6}|([a-z/]+[a-z/]{2,6}))$/,
    "Invalid Domain"
  );

export const providedDomainValidation = Yup.string()
  .required(validationMessages.required)
  .max(60, "This value is too long. It should have 60 characters or less.")
  .matches(/^[a-z0-9]+$/, "You can use only small letters or numbers.");

export const platformNames = {
  [PLATFORM_TYPE_ANDROID]: "Android",
  [PLATFORM_TYPE_ANDROID_TV]: "Fire TV",
  [PLATFORM_TYPE_TVOS]: "Apple TV",
  [PLATFORM_TYPE_GOOGLE_TV]: "Google TV",
  [PLATFORM_TYPE_IOS]: "iOS",
  [PLATFORM_TYPE_ROKU]: "Roku",
  [PLATFORM_TYPE_COBRA]: "Website",
  [PLATFORM_TYPE_CUSTOM]: "Custom",
  [PLATFORM_TYPE_EMBED_PLAYER]: "Embed Player"
};

export const getUrlForImplementationType = (implementationType) => {
  switch (implementationType) {
    case PLATFORM_TYPE_COBRA:
      return "Web";
    case PLATFORM_TYPE_ANDROID_TV:
      return "AndroidTv";
    case PLATFORM_TYPE_GOOGLE_TV:
      return "GoogleTv";
    case PLATFORM_TYPE_ANDROID:
      return "Android";
    case PLATFORM_TYPE_IOS:
      return "IOs";
    case PLATFORM_TYPE_TVOS:
      return "TvOs";
    case PLATFORM_TYPE_ROKU:
      return "Roku";
    case PLATFORM_TYPE_CUSTOM:
      return "Custom";
    case PLATFORM_TYPE_EMBED_PLAYER:
      return "EmbedPlayer";
    default:
      return "";
  }
};

const getPlaceholderForPlatform = (platformType) => {
  if (
    [
      PLATFORM_TYPE_ROKU,
      PLATFORM_TYPE_ANDROID_TV,
      PLATFORM_TYPE_IOS,
      PLATFORM_TYPE_TVOS
    ].includes(platformType)
  ) {
    return "Disabled In-App purchases";
  } else {
    return "--- Choose Payment Gateway ---";
  }
};

export const movePlatformFormConfig = (platformType, closeModal, app) => ({
  formTitle: `Move ${platformNames?.[platformType] || ""} Platform`,
  apiEndpoint: `platforms${getUrlForImplementationType(platformType)}`,
  fields: [
    {
      name: "app",
      label: "To App",
      type: "entity",
      resource: "apps?limit=50",
      placeholder: "--- Choose App ---",
      isRequired: true,
      validationSchema: "required",
      getValue: () => "",
      entityOptionsFilter: (options) => {
        return options.filter((optionApp) => optionApp.id !== app.id);
      }
    }
  ],
  buttons: [
    {
      title: "Move",
      Button: Submit
    },
    {
      title: "Cancel",
      Button: Cancel,
      onClick: closeModal
    }
  ]
});

export const getMainDomain = (loggedCommunity) => {
  return loggedCommunity?.options?.useOwnSiteDomain === false
    ? `${
        process.env.REACT_APP_STAGE === "staging" ? ".staging" : ""
      }.${loggedCommunity.group.mainDomain}`
    : "";
};

export const getPlatformFormConfig = (platformType, app, loggedCommunity) => {
  const mainDomain = getMainDomain(loggedCommunity);
  return {
    formEntity: platformNames?.[platformType] || "Platform",
    skipOptimisticUpdate: true,
    apiEndpoint: `platforms${getUrlForImplementationType(platformType)}`,
    createSideEffects: ({ navigate, openModal }) => ({
      onSuccess: (response) => {
        navigate(`/apps/${app.id}/platforms/${response.data.id}`);
        openModal({ entity: "platforms", isOpen: false });
      }
    }),
    editSideEffects: ({
      openModal,
      queryClient,
      updateItemInQueryData,
      dispatch
    }) => ({
      onSuccess: ({ data }) => {
        queryClient.invalidateQueries({ queryKey: ["webApp"] });
        updateItemInQueryData(data);
        openModal({ entity: "platforms", isOpen: false });
        dispatch(
          showToast({
            msg: `Item updated successfully.`
          })
        );
      }
    }),
    initialValues: { isActive: true },
    preSubmit: (submittedValues) => {
      if (platformType === PLATFORM_TYPE_EMBED_PLAYER) {
        return { ...submittedValues };
      }
      if (platformType === PLATFORM_TYPE_COBRA && mainDomain) {
        return {
          ...submittedValues,
          app: app["@id"],
          siteUrl: `${submittedValues.siteUrl + mainDomain}`
        };
      } else
        return {
          ...submittedValues,
          app: app["@id"]
        };
    },
    fields: [
      {
        name: "isActive",
        label: "Active",
        type: "checkbox"
      },
      {
        name: "siteUrl",
        label: "Site Domain",
        isRequired: true,
        type: "text",
        textFieldTail: mainDomain,
        isVisible: () => platformType === PLATFORM_TYPE_COBRA,
        getValue: ({ siteUrl = "" }) =>
          mainDomain && siteUrl.includes(mainDomain)
            ? siteUrl.replace(mainDomain, "")
            : siteUrl,
        ...(mainDomain
          ? { definedValidation: providedDomainValidation }
          : {
              definedValidation: ownDomainValidation
            })
      },
      {
        name: "watermarkImage",
        label: "Watermark",
        type: "file",
        fileUploadFieldNote:
          "We recommend using 24 bit 300x300px PNG image with transparency",
        getValue: (values) =>
          values?.images?.find((i) => i.fieldName === "watermarkImage")?.url,
        isVisible: () => platformType === PLATFORM_TYPE_EMBED_PLAYER
      },
      {
        name: "watermarkLink",
        label: "Watermark Link",
        type: "text",
        isVisible: () => platformType === PLATFORM_TYPE_EMBED_PLAYER,
        validationSchema: "url"
      },
      {
        name: "playlist",
        type: "autocompleteInput",
        label: "Playlist",
        resource: "playlists",
        searchParamName: "name",
        mapInitValue: (val) => ({
          mapValue: val?.playlist?.["@id"],
          mapShowValue: val?.playlist?.name
        }),
        mapResponse: (val) => ({
          label: val.name,
          value: val["@id"]
        }),
        isVisible: () =>
          ![
            PLATFORM_TYPE_COBRA,
            PLATFORM_TYPE_CUSTOM,
            PLATFORM_TYPE_EMBED_PLAYER
          ].includes(platformType)
      },
      {
        name: "bannerGroup",
        type: "autocompleteInput",
        label: "Banner Group",
        resource: "bannerGroups",
        searchParamName: "name",
        mapInitValue: (val) => ({
          mapValue: val?.bannerGroup?.["@id"],
          mapShowValue: val?.bannerGroup?.name
        }),
        mapResponse: (val) => ({
          label: val.name,
          value: val["@id"]
        }),
        isVisible: () =>
          ![
            PLATFORM_TYPE_COBRA,
            PLATFORM_TYPE_CUSTOM,
            PLATFORM_TYPE_EMBED_PLAYER,
            PLATFORM_TYPE_IOS,
            PLATFORM_TYPE_TVOS
          ].includes(platformType)
      },
      {
        name: "minimumSupportedVersion",
        label: "Minimum Supported Version",
        type: "text",
        isVisible: () =>
          ![PLATFORM_TYPE_COBRA, PLATFORM_TYPE_EMBED_PLAYER].includes(
            platformType
          )
      },
      {
        name: "ignoreGeoRestrictions",
        label: "Ignore GEO Restrictions",
        type: "checkbox",
        isVisible: () => platformType === PLATFORM_TYPE_CUSTOM
      },
      {
        name: "verificationUrl",
        label: "Verification URL",
        type: "text",
        isVisible: () => platformType === PLATFORM_TYPE_ROKU,
        validationSchema: "url"
      },
      {
        name: "defaultPaymentGateway",
        label: "Default Payment Gateway",
        type: "entity",
        placeholder: getPlaceholderForPlatform(platformType),
        resource: "paymentGateways",
        apiParams: { isActive: true },
        entityOptionsFilter: (options) => {
          let gatewayTypesToShow;
          switch (platformType) {
            case PLATFORM_TYPE_ROKU:
              gatewayTypesToShow = [GATEWAY_TYPE_ROKU];
              break;
            case PLATFORM_TYPE_ANDROID_TV:
              gatewayTypesToShow = [GATEWAY_TYPE_AMAZON];
              break;
            case PLATFORM_TYPE_IOS:
              gatewayTypesToShow = [GATEWAY_TYPE_ITUNES];
              break;
            case PLATFORM_TYPE_TVOS:
              gatewayTypesToShow = [GATEWAY_TYPE_ITUNES];
              break;
            default:
              gatewayTypesToShow = [
                GATEWAY_TYPE_STRIPE,
                GATEWAY_TYPE_TPAY,
                GATEWAY_TYPE_PAYPAL,
                GATEWAY_TYPE_AUTHORIZE
              ];
          }
          return options.filter(
            (option) =>
              gatewayTypesToShow.includes(option?.["@type"]) && option?.isActive
          );
        },
        isVisible: () =>
          ![PLATFORM_TYPE_EMBED_PLAYER, PLATFORM_TYPE_CUSTOM].includes(
            platformType
          ),
        getValue: (value) => value?.defaultPaymentGateway?.["@id"]
      },
      {
        name: "geoTemplate",
        label: "GEO Template",
        type: "entity",
        resource: "geoRestrictionTemplates",
        getValue: (value) => value?.geoTemplate?.["@id"]
      },
      {
        name: "adSchedule",
        label: "Ad Schedule",
        type: "autocompleteInput",
        resource: "adSchedules",
        searchParamName: "name",
        mapInitValue: (val) => ({
          mapValue: val?.adSchedule?.["@id"],
          mapShowValue: val?.adSchedule?.name
        }),
        mapResponse: (val) => ({
          label: val.name,
          value: val["@id"]
        }),
        isVisible: () =>
          loggedCommunity.options?.enableExternalAds ||
          loggedCommunity.options?.enableInternalAds
      },
      {
        name: "additionalHeadCode",
        label: "Additional <head> Code",
        type: "textarea",
        inputClass: "resize-vertical",
        isVisible: () => platformType === PLATFORM_TYPE_COBRA,
        definedValidation: Yup.string().max(
          2048,
          "This value should be 2048 characters max."
        )
      },
      {
        name: "additionalBodyCode",
        label: "Additional <body> Code",
        type: "textarea",
        inputClass: "resize-vertical",
        isVisible: () => platformType === PLATFORM_TYPE_COBRA,
        definedValidation: Yup.string().max(
          2048,
          "This value should be 2048 characters max."
        )
      },
      {
        name: "customData",
        label: "Custom Data",
        type: "textarea"
      }
    ]
  };
};

export const getEmbedPlatform = async () => {
  const response = await callApi({
    url: `/platformsEmbedPlayer`,
    method: "GET"
  });
  return response.data["hydra:member"][0];
};
